import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import type { Crumb } from "../utils/structuredData";
import SlotPicker from "../components/SlotPicker";
import Loader from "../components/Loader";
import BookingConfirmation from "../components/BookingConfirmation";
import ExistingMeeting from "../components/ExistingMeeting";
import GuestFields from "../components/GuestFields";
import { cleanGuests } from "../utils/guests";
import {
  type Availability,
  type Booking,
  type ExistingBooking,
  type Guest,
  browserTimezone,
  newIdempotencyKey,
  SchedulerError,
  schedulerApi,
} from "../services/schedulerApi";

const crumbs: Crumb[] = [
  { name: "Home", path: "/" },
  { name: "Book Your SOW Review", path: "/book/review" },
];

// The tokenized SOW review link emailed to the client: always books with the client's engagement lead.
const BookReview: React.FC = () => {
  const [params] = useSearchParams();
  const [token, setToken] = useState<string | null>(null);
  const [availability, setAvailability] = useState<Availability | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [booking, setBooking] = useState<Booking | null>(null);
  const [other, setOther] = useState<ExistingBooking | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [guestsInvalid, setGuestsInvalid] = useState(false);
  const [busy, setBusy] = useState(true);
  const bookingKey = useRef<{ slot: string; key: string } | null>(null);
  const timezone = useRef("America/New_York");

  // Read the link only in the browser; the page is prerendered without it.
  useEffect(() => {
    const value = params.get("token");
    timezone.current = browserTimezone();
    setToken(value);
    if (!value) {
      setNotice("This booking link is incomplete. Please use the link from your email.");
      setBusy(false);
      return;
    }
    schedulerApi.availability({ token: value, timezone: timezone.current })
      .then((result) => {
        // Already booked: show that review (this link proves who the visitor is) with its manage link, or the
        // client's other upcoming meeting (one at a time), which comes without one.
        const existing = result.existing_booking;
        if (existing && "manage_token" in existing && existing.manage_token) setBooking(existing as Booking);
        else if (existing) setOther(existing);
        else setAvailability(result);
      })
      .catch((error) => setNotice(error instanceof SchedulerError ? error.message : "Something went wrong."))
      .finally(() => setBusy(false));
  }, [params]);

  const onConfirm = async () => {
    if (!token || !selected || !availability) return;
    const invited = cleanGuests(guests);
    setGuestsInvalid(invited === null);
    if (invited === null) return;
    const attempt = `${selected}|${JSON.stringify(invited)}`;
    if (bookingKey.current?.slot !== attempt) bookingKey.current = { slot: attempt, key: newIdempotencyKey() };
    setBusy(true);
    setNotice(null);
    try {
      const result = await schedulerApi.book(
        { token, timezone: timezone.current, start: selected, ...(invited.length ? { guests: invited } : {}) },
        bookingKey.current.key);
      setBooking(result.booking);
    } catch (error) {
      if (error instanceof SchedulerError && error.code === "ALREADY_BOOKED" && error.existing?.same_person
          && "starts_at" in error.existing && error.existing.manage_token) {
        setBooking(error.existing as unknown as Booking);
        return;
      }
      if (error instanceof SchedulerError && error.code === "ALREADY_BOOKED" && error.existing) {
        setOther(error.existing);
        return;
      }
      if (error instanceof SchedulerError && error.code === "SLOT_TAKEN" && error.slots) {
        setAvailability({ ...availability, slots: error.slots });
        setSelected(null);
      }
      setNotice(error instanceof SchedulerError ? error.message : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative pt-6 pb-16">
      <Seo title="Book Your SOW Review | Contextral" description="Choose a time to review your scope of work." noindex />
      <section>
        <Breadcrumbs items={crumbs} />
        <div className="text-center mb-10 sm:mb-14">
          <h1 className="font-bold text-text-primary mb-4 text-3xl sm:text-4xl lg:text-5xl leading-tight">
            Book Your SOW Review
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-text-secondary max-w-4xl mx-auto leading-relaxed">
            {availability && !booking
              ? `Pick a ${availability.meeting_minutes}-minute slot with ${availability.lead.name} to walk through your scope of work.`
              : "We'll walk through your scope of work together."}
          </p>
        </div>
      </section>
      <section className="max-w-3xl mx-auto">
        <div className="rounded-2xl border border-borderSubtle bg-surface shadow-xl p-6 sm:p-8 md:p-10">
          {notice && <p role="alert" className="mb-6 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{notice}</p>}
          {booking ? (
            <BookingConfirmation booking={booking}
              heading={availability ? "You're booked" : "Your review is already booked"} />
          ) : other ? (
            <ExistingMeeting existing={other} />
          ) : availability ? (
            <>
              <SlotPicker slots={availability.slots} timeZone={availability.timezone} selected={selected}
                onSelect={setSelected} disabled={busy} />
              <GuestFields guests={guests} onChange={setGuests} disabled={busy} invalid={guestsInvalid} />
              <div className="mt-8 flex justify-end">
                <button type="button" onClick={() => void onConfirm()} disabled={busy || !selected}
                  className="inline-flex items-center justify-center rounded-full bg-primary px-8 py-3 font-semibold text-text-inverse hover:bg-primaryHover disabled:cursor-not-allowed disabled:opacity-60">
                  {busy ? "Booking..." : "Confirm booking"}
                </button>
              </div>
            </>
          ) : busy ? (
            <Loader label="Finding available times" compact />
          ) : null}
        </div>
      </section>
    </div>
  );
};

export default BookReview;
